import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil, debounceTime, take } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { IInventoryFilter, IProduct } from 'src/app/features/produits/models/products.models';
import { IPagination } from 'src/app/shared/models/page.model';
import { parseInventoryFilterRequest, refillInventoryFilter } from 'src/app/shared/utils/helpers';
import { SharedModule } from 'src/app/shared/shared.module';
import { environment } from 'src/environments/environment';
import { IParseProduct } from '../../models/quotations.models';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { FetchProductsBySearch, SetInventoryFilter, ClearProducts } from 'src/app/features/produits/store/produits.actions';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { ProductCodeService } from 'src/app/core/services/product-code.service';

@Component({
  selector: 'app-products-quote-from-all-products',
  templateUrl: './products-quote-from-all-products.component.html',
  styleUrls: ['./products-quote-from-all-products.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule, SvgIconComponent],
})
export class ProductsQuoteFromAllProductsComponent implements OnInit, OnDestroy {
  @Input() existingProducts: IParseProduct[] | undefined = [];
  @Input() currency?: ICurrency;
  @Output() selectedProducts = new EventEmitter<IProduct[]>();

  productsBySearch$: Observable<IProduct[]> = this.store.select((state) => state.products.productsBySearch);
  metadata$: Observable<IPagination | null> = this.store.select((state) => state.products.metadata);

  products: IProduct[] = [];
  currentFilter: IProduct[] = [];

  baseUrl = environment.BASE_URL_API;
  placeholder = 'assets/images/placeholder.png';

  nameControleProduct = new FormControl('');
  productsfilter: IInventoryFilter = refillInventoryFilter();
  isProductsFiltered = false;

  // pagination / infinite scroll
  currentPage = 1;
  pageSize = 50;
  isLoadingMore = false;
  productsMetadata: IPagination | null = null;
  hasMore = false;
  private scrollSubject = new Subject<Event>();
  private destroy$ = new Subject<void>();
  private lastScrollEmit = 0;
  private scrollThreshold = 0.9;

  // table / selection state
  selectAll = false;
  quantityForm: FormGroup;
  private selectedById = new Map<number, IProduct>();
  private draftById = new Map<number, Partial<IProduct>>();

  currentSortField: 'name' | 'price_unit' | 'final_price' | 'stock' | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    public productCode: ProductCodeService,
  ) {
    this.quantityForm = this.fb.group({});

    // Debounce scroll events to prevent too frequent loadMore calls
    this.scrollSubject
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe((event) => this.handleScroll(event));
  }

  private getAnyProductId(p: any): number | null {
    const id = p?.id ?? p?.product_id;
    return typeof id === 'number' ? id : null;
  }

  ngOnInit(): void {
    this.seedSelectionFromExistingProducts();

    this.store
      .select((state) => state.products.productFilterState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filterState) => {
        this.currentPage = 1;
        this.isLoadingMore = false;
        this.products = [];

        const filterRequest = parseInventoryFilterRequest(filterState.advanced);
        this.store.dispatch(
          FetchProductsBySearch({
            keyword: filterState.keyword || '',
            page: { number: this.currentPage, size: this.pageSize },
            filter: filterRequest,
            sort_by: 'name',
            sort_mode: 'asc',
            priority: (this.existingProducts || [])
              .map((p: any) => this.getAnyProductId(p))
              .filter((id): id is number => typeof id === 'number'),
          })
        );
      });

    // react to results
    this.productsBySearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((products) => {
        const mapped = products.map((p) => this.decorateProduct(p));
        this.products = this.isLoadingMore ? [...this.products, ...mapped] : mapped;
        this.isLoadingMore = false;
        this.applyFilter();
      });

    this.metadata$.pipe(takeUntil(this.destroy$)).subscribe((metadata) => {
      this.productsMetadata = metadata;
      this.updateHasMore();
    });

    this.nameControleProduct.valueChanges
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe((value) => {
        const key = (value || '').trim();
        this.store.dispatch(
          SetInventoryFilter({
            keyword: key,
            advanced: this.productsfilter,
          })
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(ClearProducts());
    this.store.dispatch(SetInventoryFilter({ keyword: '', advanced: refillInventoryFilter() }));
  }

  private seedSelectionFromExistingProducts(): void {
    this.selectedById.clear();
    for (const p of this.existingProducts || []) {
      const id = this.getAnyProductId(p);
      if (!id) continue;
      this.selectedById.set(id, {
        id,
        product_id: id,
        product_template_id: p.product_template_id,
        name: p.name,
        sku: p.sku,
        erp_id: p.erp_id,
        barcode: (p as any).barcode,
        image: p.image,
        images: p.images,
        cost: (p as any).cost ?? 0,
        sales_price: (p as any).sales_price ?? 0,
        final_price: p.final_price ?? null,
        discount: p.discount ?? 0,
        increment: p.increment,
        min_qte: p.min_qte,
        max_qte: p.max_qte,
        quantity: p.quantity,
        is_owned: p.is_owned,
        can_be_sold_out_of_stock: (p as any).can_be_sold_out_of_stock,
        stock: (p as any).stock,
        currencies: (p as any).currencies,
        brand_id: (p as any).brand_id,
        brand: (p as any).brand,
        categories: (p as any).categories,
        tags: (p as any).tags,
      } as any);
    }
  }

  private decorateProduct(product: IProduct): IProduct {
    const pid = this.getAnyProductId(product);
    const isSelected = !!(pid && this.selectedById.has(pid));
    const quoteCurrencyId = this.currency?.id;
    const hasQuoteCurrency =
      !!quoteCurrencyId &&
      Array.isArray(product.currencies) &&
      product.currencies.some((c) => c.currency_id === quoteCurrencyId);

    const currencyEntry = hasQuoteCurrency
      ? product.currencies?.find((c) => c.currency_id === quoteCurrencyId)
      : undefined;

    const draft = pid ? this.draftById.get(pid) : undefined;
    const merged: IProduct = {
      ...product,
      ...(draft || {}),
      quantity: (draft?.quantity as any) ?? product.quantity ?? product.min_qte ?? 0,
      selected: isSelected,
      // Keep disabled logic consistent with catalog picker
      disabled: product.status === 'Draft',
      currencies: product.currencies || [],
      sales_price: currencyEntry?.sales_price ?? product.sales_price,
      final_price: (draft?.final_price as any) ?? (currencyEntry?.final_price ?? product.final_price ?? null),
      // marker
      default_currency: {
        amount: currencyEntry?.sales_price ?? null,
        symbol: this.currency?.symbol ?? '',
      } as any,
    };

    // If this product is selected, ensure we keep the latest (API) shape in the selection map
    if (pid && this.selectedById.has(pid)) {
      const prev = this.selectedById.get(pid);
      this.selectedById.set(pid, { ...prev, ...merged, selected: true } as any);
    }

    return merged;
  }

  setFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
      data: {
        filter: this.productsfilter,
        isQuoteProducts: true,
      },
    });

    dialogRef.afterClosed().subscribe((filter: IInventoryFilter) => {
      if (filter) {
        this.productsfilter = filter;
        this.isProductsFiltered = true;
        this.store.dispatch(SetInventoryFilter({ keyword: (this.nameControleProduct.value || '').trim(), advanced: filter }));
      }
    });
  }

  clearFilter(): void {
    this.productsfilter = refillInventoryFilter();
    this.isProductsFiltered = false;
    this.store.dispatch(SetInventoryFilter({ keyword: (this.nameControleProduct.value || '').trim(), advanced: this.productsfilter }));
  }

  private applyFilter(): void {
    const q = (this.nameControleProduct.value || '').trim().toLowerCase();
    const filtered = !q
      ? this.products
      : this.products.filter((p) => p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q));
    this.currentFilter = [...filtered];
    this.applySorting();
    this.selectAll = this.currentFilter.filter((p) => !this.isSelectionDisabled(p)).every((p) => p.selected);
  }

  sort(field: 'name' | 'price_unit' | 'final_price' | 'stock' | null): void {
    if (this.currentSortField === field) {
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortField = field;
      this.currentSortDirection = 'asc';
    }
    this.applySorting();
  }

  trackByProduct(_index: number, product: IProduct): number | string {
    return product.id ?? product.product_id ?? _index;
  }

  private applySorting(): void {
    if (!this.currentSortField || !this.currentSortDirection) return;
    const dir = this.currentSortDirection === 'asc' ? 1 : -1;
    this.currentFilter.sort((a, b) => {
      let va: any;
      let vb: any;
      switch (this.currentSortField) {
        case 'name':
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
          break;
        case 'price_unit':
          va = this.getRetailPriceValue(a) ?? Number.POSITIVE_INFINITY;
          vb = this.getRetailPriceValue(b) ?? Number.POSITIVE_INFINITY;
          break;
        case 'final_price':
          va = this.getFinalPriceValue(a) ?? Number.POSITIVE_INFINITY;
          vb = this.getFinalPriceValue(b) ?? Number.POSITIVE_INFINITY;
          break;
        case 'stock':
          va = a.stock?.quantity ?? 0;
          vb = b.stock?.quantity ?? 0;
          break;
        default:
          return 0;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }

  toggleSelectAll(): void {
    const newState = !this.selectAll;
    this.currentFilter = this.currentFilter.map((p) => ({
      ...p,
      selected: !this.isSelectionDisabled(p) && newState,
    }));
    this.products = this.products.map((p) => ({
      ...p,
      selected: !this.isSelectionDisabled(p) && newState,
    }));
    this.selectAll = this.currentFilter.filter((p) => !this.isSelectionDisabled(p)).every((p) => p.selected);
    for (const p of this.currentFilter) {
      const id = this.getAnyProductId(p);
      if (!id) continue;
      if (this.isSelectionDisabled(p)) continue;
      if (newState) this.selectedById.set(id, { ...p, selected: true } as any);
      else this.selectedById.delete(id);
    }
    this.emitSelection();
  }

  toggleSelectProduct(product: IProduct): void {
    if (this.isSelectionDisabled(product)) return;
    product.selected = !product.selected;
    this.products = this.products.map((p) => (p.id === product.id ? { ...p, selected: product.selected } : p));
    this.selectAll = this.currentFilter.filter((p) => !this.isSelectionDisabled(p)).every((p) => p.selected);
    const id = this.getAnyProductId(product);
    if (id) {
      if (product.selected) this.selectedById.set(id, { ...product, selected: true } as any);
      else this.selectedById.delete(id);
    }
    this.emitSelection();
  }

  private emitSelection(): void {
    const selected = Array.from(this.selectedById.values()).filter((p) => !this.isSelectionDisabled(p));
    this.selectedProducts.emit(selected);
  }

  getSelectedProductsCount(): number {
    return Array.from(this.selectedById.values()).filter((p) => !this.isSelectionDisabled(p)).length;
  }

  // Currency-aware helpers
  private getCurrencyEntry(product: IProduct): { sales_price?: number; final_price?: number } | null {
    const currencyId = this.currency?.id;
    if (!currencyId || !product.currencies) return null;
    return product.currencies.find((c) => c.currency_id === currencyId) || null;
  }

  private getRetailPriceValue(product: IProduct): number | null {
    const entry = this.getCurrencyEntry(product);
    return entry?.sales_price ?? null;
  }

  private getFinalPriceValue(product: IProduct): number | null {
    const entry = this.getCurrencyEntry(product);
    // If quote currency is missing, we rely on product.final_price (manual)
    return entry?.final_price ?? (product.final_price ?? null);
  }

  getRetailPrice(product: IProduct): string {
    const v = this.getRetailPriceValue(product);
    if (v == null) return '--';
    return `${v} ${this.currency?.symbol ?? ''}`.trim();
  }

  getProductFinalPrice(product: IProduct): string {
    const entry = this.getCurrencyEntry(product);
    const v = entry?.final_price ?? product.final_price;
    if (v == null) return '--';
    return `${Number(v)} ${this.currency?.symbol ?? ''}`.trim();
  }

  isQuoteCurrencyMissing(product: IProduct): boolean {
    const currencyId = this.currency?.id;
    if (!currencyId) return true;
    return !(product.currencies || []).some((c) => c.currency_id === currencyId);
  }

  isFinalPriceMissing(product: IProduct): boolean {
    return this.isQuoteCurrencyMissing(product) && !(product.final_price && Number(product.final_price) > 0);
  }

  isSelectionDisabled(product: IProduct): boolean {
    return !!product.disabled || this.isFinalPriceMissing(product);
  }

  onManualFinalPriceInput(event: Event, product: IProduct): void {
    const input = event.target as HTMLInputElement;
    const v = Number(input.value);
    product.final_price = isNaN(v) ? null : v;
    this.selectAll = this.currentFilter.filter((p) => !this.isSelectionDisabled(p)).every((p) => p.selected);
    const id = this.getAnyProductId(product);
    if (id) {
      this.draftById.set(id, { ...(this.draftById.get(id) || {}), final_price: product.final_price });
      if (this.selectedById.has(id)) {
        const prev = this.selectedById.get(id)!;
        this.selectedById.set(id, { ...prev, final_price: product.final_price } as any);
        this.emitSelection();
      }
    }
  }

  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  private handleScroll(event: Event): void {
    const now = Date.now();
    if (now - this.lastScrollEmit < 500) return;
    if (this.isLoadingMore || !this.hasMore) return;

    const target = event.target as HTMLElement;
    if (!target) return;

    const scrollTop = target.scrollTop || 0;
    const clientHeight = target.clientHeight || 0;
    const scrollHeight = target.scrollHeight || 0;
    const scrollPosition = scrollTop + clientHeight;

    if (scrollHeight > 0 && scrollPosition / scrollHeight > this.scrollThreshold) {
      this.lastScrollEmit = now;
      this.loadMore();
    }
  }

  private loadMore(): void {
    if (this.isLoadingMore) return;
    if (!this.productsMetadata) return;
    if (this.products.length >= this.productsMetadata.count) return;

    this.isLoadingMore = true;
    this.currentPage += 1;

    this.store
      .select((state) => state.products.productFilterState)
      .pipe(take(1))
      .subscribe((filterState) => {
        const filterRequest = parseInventoryFilterRequest(filterState.advanced);
        this.store.dispatch(
          FetchProductsBySearch({
            keyword: filterState.keyword || '',
            page: { number: this.currentPage, size: this.pageSize },
            filter: filterRequest,
            sort_by: 'name',
            sort_mode: 'asc',
            priority: (this.existingProducts || [])
              .map((p: any) => this.getAnyProductId(p))
              .filter((id): id is number => typeof id === 'number'),
          })
        );
      });
  }

  private updateHasMore(): void {
    if (!this.productsMetadata) {
      this.hasMore = false;
      return;
    }
    this.hasMore = this.products.length < this.productsMetadata.count;
  }
}
